import { queryOptions, type QueryClient } from '@tanstack/react-query';

export const AGENDA_QUERY_KEY = ['agenda-bundle'] as const;

interface AgendaQueryScope {
  vendorId?: number;
  isVenue?: boolean;
  selectedVendorId: string | null;
  startDate: string;
  endDate: string;
  vendorIds: string;
  showResourceView: boolean;
  view: string;
}

/**
 * El bundle anterior solo sirve de placeholder si sus turnos son de lo que se ve ahora.
 * FullCalendar ya pintó las fechas nuevas: con un rango anterior que no las cubre, la
 * grilla quedaba vacía ("no tenés turnos") hasta que llegaba la respuesta. Sin
 * placeholder la query vuelve a `pending` y Agenda muestra el skeleton de la grilla.
 * Fuera de la vista por columnas, los turnos de otro profesional se leerían como del
 * elegido: también se descartan.
 */
const placeholderFits = (previousKey: readonly unknown[], scope: AgendaQueryScope) => {
  const [, vendorId, startDate, endDate, vendorIds] = previousKey;
  if (vendorId !== scope.vendorId) return false;
  if (!scope.showResourceView && scope.view !== 'dayGridMonth' && vendorIds !== scope.vendorIds) return false;
  // La vista Día pide la semana entera (Agenda.tsx: endDate = start + 6) pero muestra un día.
  const visibleEnd = scope.view === 'resourceTimeGridDay' ? scope.startDate : scope.endDate;
  return (
    typeof startDate === 'string' &&
    typeof endDate === 'string' &&
    startDate <= scope.startDate &&
    visibleEnd <= endDate
  );
};

export const agendaBundleQueryOptions = <T>(scope: AgendaQueryScope, fetchBundle: () => Promise<T>) =>
  queryOptions({
    queryKey: [
      ...AGENDA_QUERY_KEY,
      scope.vendorId,
      scope.startDate,
      scope.endDate,
      scope.vendorIds,
      scope.showResourceView
    ],
    queryFn: fetchBundle,
    enabled:
      !!scope.vendorId &&
      (!scope.isVenue ||
        scope.selectedVendorId !== null ||
        scope.showResourceView ||
        scope.view === 'dayGridMonth'),
    retry: 2,
    retryDelay: (attempt: number) => Math.min(300 * 2 ** attempt, 2000),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    placeholderData: (previousData, previousQuery) =>
      previousQuery && placeholderFits(previousQuery.queryKey, scope) ? previousData : undefined,
    staleTime: 30_000
  });

// The calendar uses the bundle; other screens still consume the individual queries.
// Inactive date ranges become stale without being fetched until they are visited.
export const invalidateAgendaQueries = (queryClient: QueryClient) =>
  Promise.all(
    [AGENDA_QUERY_KEY, ['event_instances'], ['unique_events']].map(async (queryKey) => {
      // invalidateQueries cancels background refetches, but deduplicates the
      // initial fetch when there is no cached data. That snapshot may predate
      // this booking, so cancel it explicitly before requesting fresh data.
      await queryClient.cancelQueries({
        queryKey,
        type: 'active',
        predicate: (query) => query.state.data === undefined && query.state.fetchStatus === 'fetching'
      });
      return queryClient.invalidateQueries({ queryKey });
    })
  );
